/**
 * Gráficos SVG puros (sem biblioteca): rosca, radar, velocímetro, área/linha,
 * barras divergentes/agrupadas e dispersão. Interativos: tooltip no hover e
 * legenda clicável (marca/oculta a série). Cores via CSS var.
 */
import { type MouseEvent as RMouse, type ReactNode, useState } from "react";

function polar(cx: number, cy: number, r: number, graus: number): [number, number] {
  const a = (graus * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

/** Caminho de arco (sentido horário) entre dois ângulos em graus. */
function arco(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const [x0, y0] = polar(cx, cy, r, a0);
  const [x1, y1] = polar(cx, cy, r, a1);
  const grande = a1 - a0 > 180 ? 1 : 0;
  return `M ${x0} ${y0} A ${r} ${r} 0 ${grande} 1 ${x1} ${y1}`;
}

function fmt(n: number | null): string {
  return n == null ? "—" : `${n.toFixed(0)}%`;
}

type TipFn = (e: RMouse, texto: string) => void;

/** Níveis de maturidade do Manual IMGR (Tabela 3) — faixa, cor e definição oficiais. */
export const FAIXAS_IMGR = [
  {
    nome: "Inicial",
    curto: "Inicial",
    ate: 20,
    faixa: "0–20%",
    cor: "#e23b34",
    def: "Baixo nível de prontidão; ausência de rotinas de controle, comunicação incipiente sobre riscos e segurança.",
  },
  {
    nome: "Básico",
    curto: "Básico",
    ate: 40,
    faixa: "20–40%",
    cor: "#ef8e2c",
    def: "Há percepção informal de riscos, mas sem práticas estruturadas de controle ou aprendizado organizacional.",
  },
  {
    nome: "Intermediário",
    curto: "Interm.",
    ate: 70,
    faixa: "40–70%",
    cor: "#f3c500",
    def: "A unidade possui controles operacionais e alguma cultura de reporte, mas ainda com lacunas relevantes.",
  },
  {
    nome: "Aprimorado",
    curto: "Aprim.",
    ate: 90,
    faixa: "70–90%",
    cor: "#74bd6a",
    def: "Controles bem estabelecidos, cultura de segurança presente e práticas de aprendizado organizacional ativas.",
  },
  {
    nome: "Avançado",
    curto: "Avanç.",
    ate: 100,
    faixa: "90–100%",
    cor: "#2e9e4f",
    def: "Prontidão plena para a implantação de gestão formal de riscos; cultura de segurança integrada às rotinas.",
  },
] as const;

/** Classifica um % no nível de maturidade (cor/rótulo). */
export function faixaImgrCor(v: number | null): { nome: string; curto: string; cor: string } {
  if (v == null) {
    return { nome: "—", curto: "—", cor: "var(--surface-2)" };
  }
  return FAIXAS_IMGR.find((f) => v <= f.ate) ?? { nome: "Avançado", curto: "Avanç.", cor: "#2e9e4f" };
}

/** Legenda das 5 faixas (Tabela 3); hover no chip mostra a definição do nível. */
export function LegendaFaixas() {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
      {FAIXAS_IMGR.map((f) => (
        <span
          key={f.nome}
          title={f.def}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, cursor: "help" }}
        >
          <span style={{ width: 12, height: 12, borderRadius: 3, background: f.cor, flexShrink: 0 }} />
          <span>
            <b>{f.nome}</b> {f.faixa}
          </span>
        </span>
      ))}
    </div>
  );
}

/** Bloco expansível com a definição de cada nível (coluna "Definição" da Tabela 3). */
export function DefinicoesNiveis() {
  return (
    <details style={{ marginTop: 8 }}>
      <summary className="spec" style={{ cursor: "pointer" }}>
        Ver a definição de cada nível (Manual IMGR · Tabela 3)
      </summary>
      <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
        {FAIXAS_IMGR.map((f) => (
          <div key={f.nome} style={{ display: "flex", gap: 8, fontSize: 12, lineHeight: 1.35 }}>
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                background: f.cor,
                flexShrink: 0,
                marginTop: 2,
              }}
            />
            <span>
              <b>{f.nome}</b> ({f.faixa}) — {f.def}
            </span>
          </div>
        ))}
      </div>
    </details>
  );
}

export interface GrupoClassificado {
  readonly rotulo: string;
  readonly valores: readonly { readonly nome: string; readonly valor: number | null }[];
}

/** Barras classificadas pelo nível de maturidade (Tabela 3): cor = nível. Interativo. */
export function BarrasClassificadas({ grupos }: { grupos: readonly GrupoClassificado[] }) {
  return (
    <GrafWrap>
      {(tip, off) => (
        <div style={{ display: "grid", gap: 4 }}>
          {grupos.map((g) => (
            <div key={g.rotulo} style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 2 }}>{g.rotulo}</div>
              {g.valores.map((v) => {
                const f = faixaImgrCor(v.valor);
                return (
                  <div
                    key={v.nome}
                    style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2, cursor: "default" }}
                    onMouseMove={(e) =>
                      tip(
                        e,
                        `${g.rotulo} · ${v.nome}: ${v.valor == null ? "—" : `${v.valor.toFixed(0)}%`} · ${f.nome}`,
                      )
                    }
                    onMouseLeave={off}
                  >
                    <span style={{ width: 56, fontSize: 11, fontWeight: 700, color: "var(--text-muted)" }}>
                      {v.nome}
                    </span>
                    <div
                      style={{
                        flex: 1,
                        height: 12,
                        borderRadius: 99,
                        background: "var(--surface-2)",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${v.valor ?? 0}%`,
                          background: f.cor,
                          borderRadius: 99,
                        }}
                      />
                    </div>
                    <b style={{ width: 96, textAlign: "right", fontSize: 11 }}>
                      {v.valor == null ? "—" : `${v.valor.toFixed(0)}%`} · {f.curto}
                    </b>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </GrafWrap>
  );
}

/** Envolve um gráfico e gerencia o tooltip flutuante (posição relativa ao próprio gráfico). */
function GrafWrap({ children }: { children: (tip: TipFn, off: () => void) => ReactNode }) {
  const [t, setT] = useState<{ x: number; y: number; texto: string } | null>(null);
  const tip: TipFn = (e, texto) => {
    const wrap = (e.currentTarget as Element).closest(".graf-wrap") as HTMLElement | null;
    if (!wrap) {
      return;
    }
    const r = wrap.getBoundingClientRect();
    setT({ x: e.clientX - r.left, y: e.clientY - r.top, texto });
  };
  return (
    <div className="graf-wrap">
      {children(tip, () => setT(null))}
      {t && (
        <div className="graf-tip" style={{ left: t.x, top: t.y }}>
          {t.texto}
        </div>
      )}
    </div>
  );
}

export interface FatiaGrafico {
  readonly rotulo: string;
  readonly n: number;
  readonly cor: string;
}

/** Rosca (donut): hover mostra o valor; clicar na legenda destaca a fatia. */
export function Rosca({
  itens,
  tamanho = 132,
  espessura = 22,
}: {
  itens: readonly FatiaGrafico[];
  tamanho?: number;
  espessura?: number;
}) {
  const [destaque, setDestaque] = useState<string | null>(null);
  const total = itens.reduce((s, i) => s + i.n, 0);
  const cx = tamanho / 2;
  const cy = tamanho / 2;
  const r = (tamanho - espessura) / 2;
  const circ = 2 * Math.PI * r;
  let acc = 0;
  return (
    <GrafWrap>
      {(tip, off) => (
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <svg
            width={tamanho}
            height={tamanho}
            viewBox={`0 0 ${tamanho} ${tamanho}`}
            style={{ flexShrink: 0 }}
          >
            <title>Distribuição por categoria</title>
            <g transform={`rotate(-90 ${cx} ${cy})`}>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--surface-2)" strokeWidth={espessura} />
              {total > 0 &&
                itens.map((i) => {
                  const len = (i.n / total) * circ;
                  const op = destaque && destaque !== i.rotulo ? 0.25 : 1;
                  const el = (
                    <circle
                      key={i.rotulo}
                      cx={cx}
                      cy={cy}
                      r={r}
                      fill="none"
                      stroke={i.cor}
                      strokeWidth={espessura}
                      strokeOpacity={op}
                      strokeDasharray={`${len} ${circ - len}`}
                      strokeDashoffset={-acc}
                      style={{ cursor: "pointer", transition: "stroke-opacity .15s" }}
                      onMouseMove={(e) => tip(e, `${i.rotulo}: ${i.n} (${Math.round((i.n / total) * 100)}%)`)}
                      onMouseLeave={off}
                    />
                  );
                  acc += len;
                  return el;
                })}
            </g>
            <text x={cx} y={cy - 2} textAnchor="middle" fontSize={22} fontWeight={800} fill="var(--text)">
              {total}
            </text>
            <text x={cx} y={cy + 14} textAnchor="middle" fontSize={10} fill="var(--text-muted)">
              respostas
            </text>
          </svg>
          <div style={{ flex: 1, display: "grid", gap: 4 }}>
            {itens.map((i) => (
              <button
                type="button"
                key={i.rotulo}
                className="graf-leg"
                onClick={() => setDestaque((d) => (d === i.rotulo ? null : i.rotulo))}
                style={{ opacity: destaque && destaque !== i.rotulo ? 0.4 : 1 }}
              >
                <span style={{ width: 10, height: 10, borderRadius: 3, background: i.cor, flexShrink: 0 }} />
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {i.rotulo}
                </span>
                <b>
                  {i.n}
                  {total > 0 ? ` · ${Math.round((i.n / total) * 100)}%` : ""}
                </b>
              </button>
            ))}
          </div>
        </div>
      )}
    </GrafWrap>
  );
}

export interface SerieRadar {
  readonly nome: string;
  readonly cor: string;
  readonly valores: readonly (number | null)[]; // 0..100, alinhado a `eixos`
}

/** Radar/teia: legenda clicável oculta/mostra a série; hover nos vértices mostra o valor. */
export function Radar({
  eixos,
  series,
  tamanho = 250,
  max = 100,
}: {
  eixos: readonly string[];
  series: readonly SerieRadar[];
  tamanho?: number;
  max?: number;
}) {
  const [ocultas, setOcultas] = useState<Set<string>>(new Set());
  const cx = tamanho / 2;
  const cy = tamanho / 2;
  const R = tamanho / 2 - 34;
  const n = eixos.length;
  const ang = (i: number) => -90 + (i * 360) / n;
  const ponto = (i: number, v: number): [number, number] => polar(cx, cy, (Math.max(0, v) / max) * R, ang(i));
  const poligono = (vals: readonly (number | null)[]) =>
    vals.map((v, i) => ponto(i, v ?? 0).join(",")).join(" ");

  return (
    <GrafWrap>
      {(tip, off) => (
        <div>
          <svg width="100%" viewBox={`0 0 ${tamanho} ${tamanho}`} style={{ display: "block" }}>
            <title>Radar de maturidade por componente</title>
            {[0.25, 0.5, 0.75, 1].map((f) => (
              <polygon
                key={f}
                points={eixos.map((_, i) => polar(cx, cy, f * R, ang(i)).join(",")).join(" ")}
                fill="none"
                stroke="var(--surface-2)"
                strokeWidth={1}
              />
            ))}
            {eixos.map((rot, i) => {
              const [ex, ey] = polar(cx, cy, R, ang(i));
              const [lx, ly] = polar(cx, cy, R + 14, ang(i));
              const c = Math.cos((ang(i) * Math.PI) / 180);
              const anchor = Math.abs(c) < 0.3 ? "middle" : c > 0 ? "start" : "end";
              return (
                <g key={rot}>
                  <line x1={cx} y1={cy} x2={ex} y2={ey} stroke="var(--surface-2)" strokeWidth={1} />
                  <text
                    x={lx}
                    y={ly}
                    textAnchor={anchor}
                    dominantBaseline="middle"
                    fontSize={11}
                    fill="var(--text-muted)"
                  >
                    {rot}
                  </text>
                </g>
              );
            })}
            {series
              .filter((s) => !ocultas.has(s.nome))
              .map((s) => (
                <g key={s.nome}>
                  <polygon
                    points={poligono(s.valores)}
                    fill={s.cor}
                    fillOpacity={0.18}
                    stroke={s.cor}
                    strokeWidth={2}
                    strokeLinejoin="round"
                  />
                  {s.valores.map((v, i) => {
                    const [px, py] = ponto(i, v ?? 0);
                    return (
                      <circle
                        key={eixos[i] ?? i}
                        cx={px}
                        cy={py}
                        r={4}
                        fill={s.cor}
                        style={{ cursor: "pointer" }}
                        onMouseMove={(e) => tip(e, `${s.nome} · ${eixos[i]}: ${fmt(v)}`)}
                        onMouseLeave={off}
                      />
                    );
                  })}
                </g>
              ))}
          </svg>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 4, flexWrap: "wrap" }}>
            {series.map((s) => (
              <button
                type="button"
                key={s.nome}
                className="graf-leg"
                onClick={() =>
                  setOcultas((o) => {
                    const next = new Set(o);
                    next.has(s.nome) ? next.delete(s.nome) : next.add(s.nome);
                    return next;
                  })
                }
                style={{ opacity: ocultas.has(s.nome) ? 0.4 : 1 }}
              >
                <span style={{ width: 10, height: 10, borderRadius: 3, background: s.cor }} />
                <span style={{ color: s.cor, fontWeight: 700 }}>{s.nome}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </GrafWrap>
  );
}

/** Velocímetro semicircular (0–100); hover mostra o valor exato. */
export function Velocimetro({
  valor,
  cor,
  legenda,
  tamanho = 168,
}: {
  valor: number | null;
  cor: string;
  legenda?: string;
  tamanho?: number;
}) {
  const cx = tamanho / 2;
  const r = tamanho / 2 - 14;
  const cy = r + 14;
  const v = Math.max(0, Math.min(100, valor ?? 0));
  const fim = 180 + (v / 100) * 180;
  return (
    <GrafWrap>
      {(tip, off) => (
        <div style={{ textAlign: "center" }}>
          <svg width={tamanho} height={cy + 18} viewBox={`0 0 ${tamanho} ${cy + 18}`}>
            <title>Velocímetro</title>
            <path
              d={arco(cx, cy, r, 180, 360)}
              fill="none"
              stroke="var(--surface-2)"
              strokeWidth={14}
              strokeLinecap="round"
            />
            {valor != null && (
              <path
                d={arco(cx, cy, r, 180, fim)}
                fill="none"
                stroke={cor}
                strokeWidth={14}
                strokeLinecap="round"
                style={{ cursor: "pointer" }}
                onMouseMove={(e) => tip(e, `${valor.toFixed(1).replace(".", ",")}%`)}
                onMouseLeave={off}
              />
            )}
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize={26} fontWeight={800} fill="var(--text)">
              {valor == null ? "—" : `${valor.toFixed(0)}`}
            </text>
            <text x={cx} y={cy + 13} textAnchor="middle" fontSize={11} fill="var(--text-muted)">
              {legenda ?? "de 100"}
            </text>
          </svg>
        </div>
      )}
    </GrafWrap>
  );
}

export interface PontoSerie {
  readonly rotulo: string;
  readonly valor: number;
}

/** Área/linha temporal; hover nos pontos mostra dia e valor. */
export function AreaLinha({
  pontos,
  cor = "var(--primary)",
  altura = 96,
}: {
  pontos: readonly PontoSerie[];
  cor?: string;
  altura?: number;
}) {
  const L = 300;
  const pad = 10;
  const baixo = altura - 18;
  const max = Math.max(...pontos.map((p) => p.valor), 1);
  const n = pontos.length;
  const x = (i: number) => (n <= 1 ? L / 2 : pad + (i * (L - 2 * pad)) / (n - 1));
  const y = (val: number) => pad + (1 - val / max) * (baixo - pad);
  const linha = pontos.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p.valor)}`).join(" ");
  const area = `${linha} L ${x(n - 1)} ${baixo} L ${x(0)} ${baixo} Z`;
  return (
    <GrafWrap>
      {(tip, off) => (
        <svg width="100%" viewBox={`0 0 ${L} ${altura}`} style={{ display: "block" }}>
          <title>Respostas ao longo do tempo</title>
          <path d={area} fill={cor} fillOpacity={0.14} stroke="none" />
          <path
            d={linha}
            fill="none"
            stroke={cor}
            strokeWidth={2.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {pontos.map((p, i) => (
            <g key={p.rotulo}>
              <circle
                cx={x(i)}
                cy={y(p.valor)}
                r={4}
                fill={cor}
                style={{ cursor: "pointer" }}
                onMouseMove={(e) => tip(e, `${p.rotulo}: ${p.valor}`)}
                onMouseLeave={off}
              />
              {(i === 0 || i === n - 1 || n <= 5) && (
                <text x={x(i)} y={altura - 4} textAnchor="middle" fontSize={10} fill="var(--text-muted)">
                  {p.rotulo}
                </text>
              )}
            </g>
          ))}
        </svg>
      )}
    </GrafWrap>
  );
}

export interface ItemDivergente {
  readonly rotulo: string;
  readonly valor: number;
}

/** Barras divergentes (tornado); hover mostra o rótulo completo + valor. */
export function BarrasDivergentes({
  itens,
  corPos = "var(--primary)",
  corNeg = "var(--mat-3)",
  sufixo = "",
}: {
  itens: readonly ItemDivergente[];
  corPos?: string;
  corNeg?: string;
  sufixo?: string;
}) {
  if (itens.length === 0) {
    return <p className="spec">Sem dados para comparar.</p>;
  }
  const max = Math.max(...itens.map((i) => Math.abs(i.valor)), 1);
  return (
    <GrafWrap>
      {(tip, off) => (
        <div style={{ display: "grid", gap: 10 }}>
          {itens.map((i) => (
            <div
              key={i.rotulo}
              style={{ cursor: "default" }}
              onMouseMove={(e) =>
                tip(e, `${i.rotulo}: ${i.valor > 0 ? "+" : ""}${i.valor.toFixed(0)}${sufixo}`)
              }
              onMouseLeave={off}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 8,
                  fontSize: 12.5,
                  marginBottom: 3,
                }}
              >
                <span
                  style={{
                    flex: 1,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.25,
                  }}
                >
                  {i.rotulo}
                </span>
                <b style={{ whiteSpace: "nowrap", color: i.valor >= 0 ? corPos : corNeg }}>
                  {i.valor > 0 ? "+" : ""}
                  {i.valor.toFixed(0)}
                  {sufixo}
                </b>
              </div>
              <div style={{ display: "flex", alignItems: "center", height: 12 }}>
                <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                  <div
                    style={{
                      height: 12,
                      borderRadius: 3,
                      width: `${(Math.max(0, -i.valor) / max) * 100}%`,
                      background: corNeg,
                    }}
                  />
                </div>
                <div style={{ width: 1, alignSelf: "stretch", background: "var(--text-faint)" }} />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      height: 12,
                      borderRadius: 3,
                      width: `${(Math.max(0, i.valor) / max) * 100}%`,
                      background: corPos,
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </GrafWrap>
  );
}

export interface ItemAgrupado {
  readonly rotulo: string;
  readonly a: number;
  readonly b: number;
}

/** Barras agrupadas (duas séries); legenda clicável oculta uma série; hover mostra valores. */
export function BarrasAgrupadas({
  itens,
  corA = "var(--primary)",
  corB = "var(--mat-3)",
  nomeA = "A",
  nomeB = "B",
}: {
  itens: readonly ItemAgrupado[];
  corA?: string;
  corB?: string;
  nomeA?: string;
  nomeB?: string;
}) {
  const [verA, setVerA] = useState(true);
  const [verB, setVerB] = useState(true);
  const max = Math.max(...itens.flatMap((i) => [verA ? i.a : 0, verB ? i.b : 0]), 1);
  const linha = (cor: string, nome: string, v: number, tip: TipFn, off: () => void, rotulo: string) => (
    <div
      style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}
      onMouseMove={(e) => tip(e, `${rotulo} · ${nome}: ${v}`)}
      onMouseLeave={off}
    >
      <span style={{ width: 44, fontSize: 11, color: cor, fontWeight: 700 }}>{nome}</span>
      <div
        style={{ flex: 1, height: 9, borderRadius: 99, background: "var(--surface-2)", overflow: "hidden" }}
      >
        <div style={{ height: "100%", width: `${(v / max) * 100}%`, background: cor, borderRadius: 99 }} />
      </div>
      <b style={{ width: 22, textAlign: "right", fontSize: 12 }}>{v}</b>
    </div>
  );
  return (
    <GrafWrap>
      {(tip, off) => (
        <div style={{ display: "grid", gap: 4 }}>
          <div style={{ display: "flex", gap: 12, marginBottom: 2 }}>
            <button
              type="button"
              className="graf-leg"
              onClick={() => setVerA((x) => !x)}
              style={{ opacity: verA ? 1 : 0.4 }}
            >
              <span style={{ width: 10, height: 10, borderRadius: 3, background: corA }} />
              <span style={{ color: corA, fontWeight: 700 }}>{nomeA}</span>
            </button>
            <button
              type="button"
              className="graf-leg"
              onClick={() => setVerB((x) => !x)}
              style={{ opacity: verB ? 1 : 0.4 }}
            >
              <span style={{ width: 10, height: 10, borderRadius: 3, background: corB }} />
              <span style={{ color: corB, fontWeight: 700 }}>{nomeB}</span>
            </button>
          </div>
          {itens.map((i) => (
            <div key={i.rotulo} style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
                <b>{i.rotulo}</b>
                {i.a + i.b === 0 && (
                  <span style={{ color: "var(--danger)", fontSize: 11, fontWeight: 700 }}>sem resposta</span>
                )}
              </div>
              {verA && linha(corA, nomeA, i.a, tip, off, i.rotulo)}
              {verB && linha(corB, nomeB, i.b, tip, off, i.rotulo)}
            </div>
          ))}
        </div>
      )}
    </GrafWrap>
  );
}

export interface PontoDispersao {
  readonly x: number; // 0..100
  readonly y: number; // 0..100
  readonly rotulo: string;
}

/** Dispersão (scatter) com diagonal de referência (y = x); hover detalha o ponto. */
export function Dispersao({
  pontos,
  rotuloX = "Liderado",
  rotuloY = "Gestor",
  cor = "var(--primary)",
  tamanho = 260,
  rotulos = true,
  raio = 6,
}: {
  pontos: readonly PontoDispersao[];
  rotuloX?: string;
  rotuloY?: string;
  cor?: string;
  tamanho?: number;
  /** Rótulo em cada ponto. Desligue (`false`) quando houver muitos pontos — o
   *  rótulo continua no hover; senão os textos se sobrepõem (scatter denso). */
  rotulos?: boolean;
  /** Raio do marcador; use menor em nuvens densas para reduzir sobreposição. */
  raio?: number;
}) {
  const S = tamanho;
  const pad = 30;
  const x = (v: number) => pad + (v / 100) * (S - 2 * pad);
  const y = (v: number) => S - pad - (v / 100) * (S - 2 * pad);
  return (
    <GrafWrap>
      {(tip, off) => (
        <svg width="100%" viewBox={`0 0 ${S} ${S}`} style={{ display: "block" }}>
          <title>
            Dispersão {rotuloY} × {rotuloX} por componente
          </title>
          <line x1={x(50)} y1={y(0)} x2={x(50)} y2={y(100)} stroke="var(--surface-2)" strokeWidth={1} />
          <line x1={x(0)} y1={y(50)} x2={x(100)} y2={y(50)} stroke="var(--surface-2)" strokeWidth={1} />
          <line x1={x(0)} y1={y(0)} x2={x(100)} y2={y(0)} stroke="var(--text-faint)" strokeWidth={1} />
          <line x1={x(0)} y1={y(0)} x2={x(0)} y2={y(100)} stroke="var(--text-faint)" strokeWidth={1} />
          <line
            x1={x(0)}
            y1={y(0)}
            x2={x(100)}
            y2={y(100)}
            stroke="var(--text-faint)"
            strokeWidth={1}
            strokeDasharray="4 4"
          />
          {pontos.map((p, idx) => (
            <g key={`${p.rotulo}-${idx}`}>
              <circle
                cx={x(p.x)}
                cy={y(p.y)}
                r={raio}
                fill={cor}
                fillOpacity={0.7}
                stroke="var(--surface)"
                strokeWidth={1}
                style={{ cursor: "pointer" }}
                onMouseMove={(e) => tip(e, `${p.rotulo} · ${rotuloY} ${fmt(p.y)} × ${rotuloX} ${fmt(p.x)}`)}
                onMouseLeave={off}
              />
              {rotulos ? (
                <text x={x(p.x) + 8} y={y(p.y) + 3} fontSize={10} fontWeight={700} fill="var(--text)">
                  {p.rotulo}
                </text>
              ) : null}
            </g>
          ))}
          <text x={S / 2} y={S - 6} textAnchor="middle" fontSize={11} fill="var(--text-muted)">
            {rotuloX} →
          </text>
          <text
            x={12}
            y={S / 2}
            textAnchor="middle"
            fontSize={11}
            fill="var(--text-muted)"
            transform={`rotate(-90 12 ${S / 2})`}
          >
            {rotuloY} →
          </text>
        </svg>
      )}
    </GrafWrap>
  );
}
